class StoreLocator extends HTMLElement {
  constructor() {
    super();
    this.lastCoords = null; // No default location
    this.onClickGeo = this.onClickGeo.bind(this);
    this.onClickSearch = this.onClickSearch.bind(this);
    this.onEnterKey = this.onEnterKey.bind(this);
    this.onChangeFilters = this.onChangeFilters.bind(this);
    this.onVariantChanged = this.onVariantChanged.bind(this);
    this.lastSearchLabel = null;
  }

  // Safe JSON parsing method
  parseJSON(selector, fallback = {}) {
    try {
      const el = document.querySelector(selector);
      return el ? JSON.parse(el.textContent) : fallback;
    } catch (err) {
      console.error(`Error parsing JSON from ${selector}:`, err);
      return fallback;
    }
  }

  async connectedCallback() {
    this.initialized = true;
    this.productData = JSON.parse(
      document.querySelector("#product-json")?.textContent || "{}"
    );

    const fullData = this.parseJSON("#store-locator-data", {});

    this.storeData = fullData.stores || [];
    this.availabilityMap = fullData.availability || {};
    this.apiKey = fullData.settings?.apiKey || "";
    this.buttonText1 = fullData.settings?.buttonText1 || "See itinerary";
    this.buttonText2 = fullData.settings?.buttonText2 || "See store hours";
    this.locale = fullData.locale || "en";

    this.availabilityLabel = fullData.labels?.availability || "Availability";
    this.inStockLabel = fullData.labels?.inStock || "In Stock";
    this.outOfStockLabel = fullData.labels?.outOfStock || "Out of Stock";
    this.isFrench = this.locale?.toLowerCase().startsWith("fr");
    this.headingLabel = fullData.labels?.heading || "Stores Near %{location}";
    this.headingFallback =
      fullData.labels?.headingFallback || "In-Stock Stores";
    this.storeCountLabel = fullData.labels?.storeCount || "stores";
    this.storeFoundLabel = fullData.labels?.storeFound || "found";
    this.noResultsLabel = fullData.labels?.noResults || "No stores found.";
    this.addressLabel = fullData.labels?.addressLabel || "Coordinates";

    this.currentVariantId = document.querySelector("[name='id']")?.value;

    this.mapIcon = document.querySelector("#map-icon").innerHTML;
    this.phoneIcon = document.querySelector("#phone-icon").innerHTML;

    // Get UI refs
    this.searchInput = this.querySelector("#search-input");
    this.radiusSelect = this.querySelector("#radius-filter");
    this.resultsSelect = this.querySelector("#results-filter");
    // Disable radius filter until we have a real location
    if (this.radiusSelect) this.radiusSelect.disabled = true;

    // Bind events...
    this.querySelector("#use-location")?.addEventListener(
      "click",
      this.onClickGeo
    );
    this.querySelector("#search-location")?.addEventListener(
      "click",
      this.onClickSearch
    );
    this.searchInput?.addEventListener("keydown", this.onEnterKey);
    this.radiusSelect?.addEventListener("change", this.onChangeFilters);
    this.resultsSelect?.addEventListener("change", this.onChangeFilters);
    document.addEventListener("variant:changed", this.onVariantChanged);

    await this.initializeWithGeolocation();

    // Initial render
    this.renderFilteredStores();
  }

  disconnectedCallback() {
    this.querySelector("#use-location")?.removeEventListener(
      "click",
      this.onClickGeo
    );
    this.querySelector("#search-location")?.removeEventListener(
      "click",
      this.onClickSearch
    );
    this.searchInput?.removeEventListener("keydown", this.onEnterKey);
    this.radiusSelect?.removeEventListener("change", this.onChangeFilters);
    this.resultsSelect?.removeEventListener("change", this.onChangeFilters);
    document.removeEventListener("variant:changed", this.onVariantChanged);
  }

  onVariantChanged(e) {
    this.currentVariantId = e.detail.variant.id;
    this.updateProductHeader(e.detail.variant);
    if (this.initialized) {
      // Re-render stores filtered by current variant
      this.renderFilteredStores();
    }
  }

  updateProductHeader(variant) {
    const headerEl = this.querySelector("#store-locator-title-block");
    if (!headerEl || !variant) return;

    // Update product title
    const titleEl = headerEl.querySelector("#StoreLocatorProductTitle");
    if (titleEl) {
      // Keep the original title from Liquid if you want, or just overwrite
      titleEl.textContent = this.productData?.title || titleEl.textContent;
    }

    // Update variant options (only the <span> values)
    const optionEls = headerEl.querySelectorAll(
      ".store-locator-variant [data-option-index]"
    );
    if (optionEls.length && variant.options) {
      optionEls.forEach((span, i) => {
        if (variant.options[i]) {
          span.textContent = variant.options[i];
        }
      });
    }
  }

  onClickGeo() {
    this.handleLocationSearch(true);
  }

  // Add or replace starting here
  onClickSearch() {
    this.handleLocationSearch(false);
  }

  onEnterKey(e) {
    if (e.key === "Enter") {
      this.handleLocationSearch(false);
    }
  }

  onChangeFilters() {
    // Keep manual search context if input not empty
    const query = this.searchInput?.value.trim();
    if (query && (!this.matchedStores || !this.matchedStores.length)) {
      // re-trigger manual search if user typed something
      this.handleLocationSearch(false);
    } else {
      this.renderFilteredStores();
    }
  }

  getAvailabilityStatus(locationId) {
    if (!this.availabilityMap || !this.currentVariantId) return "Out of Stock";

    const variantData = Array.isArray(
      this.availabilityMap[this.currentVariantId]
    )
      ? this.availabilityMap[this.currentVariantId]
      : [];

    const entry = variantData.find(
      (loc) => String(loc.location) === String(locationId)
    );

    return entry && entry.qty > 0 ? "In Stock" : "Out of Stock";
  }

  updateStoreStatuses() {
    const storeResults = this.querySelector("#store-results");
    if (!storeResults) return;

    const rendered = Array.from(storeResults.querySelectorAll(".store-result"));
    rendered.forEach((el) => {
      const locId = el.dataset.locationId;
      const statusEl = el.querySelector(".stock-status");
      if (statusEl) {
        statusEl.textContent = this.getAvailabilityStatus(locId);
      }
    });
  }

  async initializeWithGeolocation() {
    const locationData = await this.getUserLocation();
    this.isFrench = this.locale?.toLowerCase().startsWith("fr");

    if (locationData.status === "OK") {
      // We have a real location
      this.lastCoords = {
        lat: locationData.latitude,
        long: locationData.longitude,
      };
      const isFrench = this.locale?.toLowerCase().startsWith("fr");
      this.lastSearchLabel = isFrench ? "Vous" : "You";
      if (this.radiusSelect) this.radiusSelect.disabled = false;
    } else {
      // No geolocation (denied or unavailable)
      this.lastCoords = null;
      this.lastSearchLabel = null;
      if (this.radiusSelect) this.radiusSelect.disabled = true;
    }

    if (this.searchInput) this.searchInput.value = "";
    this.renderFilteredStores();
  }

  /** Applies filters and renders */
  renderFilteredStores() {
    const radius = parseFloat(this.radiusSelect.value);
    const maxResults = parseInt(this.resultsSelect.value) || Infinity;

    let storesToShow = this.storeData;

    // When we have manually matched stores (city/pincode search)
    if (this.matchedStores && this.matchedStores.length) {
      storesToShow = this.matchedStores;

      // Calculate distances using user's lastCoords (geolocation)
      if (this.lastCoords) {
        storesToShow = storesToShow.map((store) => {
          const distance = this.calcDistance(this.lastCoords, {
            lat: store.latitude,
            long: store.longitude,
          });
          return { ...store, distance };
        });

        // Filter stores within radius
        storesToShow = storesToShow.filter((s) => s.distance <= radius);

        // Sort by nearest first
        storesToShow.sort((a, b) => a.distance - b.distance);
      }

      // Render top N results
      this.renderResults(storesToShow.slice(0, maxResults));
      // DO NOT RESET matchedStores HERE
      return;
    }

    // For "Use My Location" or default initialization
    if (this.lastCoords) {
      // Location-based filtering (geolocation active)
      const sortedStores = this.getStoresByLocation(
        this.lastCoords,
        this.storeData
      )
        .filter((s) => s.distance <= radius)
        .slice(0, maxResults)
        .map((s) => ({ ...s.store, distance: s.distance }));

      this.renderResults(sortedStores);
    } else {
      // No location: show all in-stock stores, ignore radius
      const inStockStores = this.storeData.filter(
        (store) => this.getAvailabilityStatus(store.store_id) === "In Stock"
      );
      this.renderResults(inStockStores);
    }
  }

  /** Main search handler for both geolocation and manual search */
  async handleLocationSearch(useMyLocation) {
    if (useMyLocation) {
      const locationData = await this.getUserLocation();
      if (locationData.status !== "OK") {
        alert(`Error: ${locationData.message}`);
        return;
      }
      this.lastCoords = {
        lat: locationData.latitude,
        long: locationData.longitude,
      };
      this.isFrench = this.locale?.toLowerCase().startsWith("fr");
      this.lastSearchLabel = isFrench ? "Vous" : "You";
      this.matchedStores = null; // reset manual search
      this.renderFilteredStores();
      return;
    }

    // Manual search by city or postal code
    const query = this.searchInput.value.trim().toLowerCase();
    if (!query) {
      alert("Please enter a city or postal code");
      return;
    }

    const matchedStores = this.storeData.filter((store) => {
      const city = (store.city || "").toLowerCase();
      const zip = (store.zip || "").toLowerCase();
      return city.includes(query) || zip.includes(query);
    });

    const resultsContainer = this.querySelector("#store-results");

    if (!matchedStores.length) {
      resultsContainer.innerHTML = `<p class="store-count">${this.noResultsLabel}</p>`;
      this.matchedStores = [];
      return;
    }

    // Save matched stores and display
    this.matchedStores = matchedStores;
    this.lastSearchLabel = query;
    this.renderFilteredStores();
  }

  /** Geocoding */
  async lookupLocation(query) {
    if (typeof google === "undefined" || !google.maps) {
      console.error("Google Maps API not loaded");
      return null;
    }

    try {
      const geocoder = new google.maps.Geocoder();
      const result = await geocoder.geocode({ address: query });

      if (!result || !result.results || !result.results.length) {
        console.warn("No geocoding results for:", query);
        return null;
      }

      const loc = result.results[0].geometry.location;
      return { lat: loc.lat(), long: loc.lng() };
    } catch (e) {
      console.error("Geocode error", e);
      return null;
    }
  }

  /** User location */
  async getUserLocation() {
    if (!navigator.geolocation) {
      return { status: "ERROR", message: "Geolocation API unavailable" };
    }
    return new Promise((resolve) => {
      navigator.geolocation.getCurrentPosition(
        (pos) =>
          resolve({
            status: "OK",
            latitude: pos.coords.latitude,
            longitude: pos.coords.longitude,
          }),
        () =>
          resolve({
            status: "ERROR",
            message: "User denied the request for Geolocation.",
          })
      );
    });
  }

  /** Store distance sorting */
  getStoresByLocation(point, allStores) {
    return allStores
      .map((store, idx) => ({
        idx,
        distance: this.calcDistance(point, {
          lat: store.latitude,
          long: store.longitude,
        }),
      }))
      .sort((a, b) => a.distance - b.distance)
      .map((entry) => ({
        distance: entry.distance,
        store: allStores[entry.idx],
      }));
  }

  /** Haversine distance */
  calcDistance(coord1, coord2) {
    const R = 6371;
    const deg2rad = (deg) => deg * (Math.PI / 180);
    const dLat = deg2rad(coord2.lat - coord1.lat);
    const dLon = deg2rad(coord2.long - coord1.long);
    const a =
      Math.sin(dLat / 2) ** 2 +
      Math.cos(deg2rad(coord1.lat)) *
        Math.cos(deg2rad(coord2.lat)) *
        Math.sin(dLon / 2) ** 2;
    const distance = R * (2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a)));
    return distance;
  }

  /** Render results to DOM */
  renderResults(stores) {
    const resultsContainer = this.querySelector("#store-results");
    if (!resultsContainer) return;

    const heading = `<h3>${
      this.lastSearchLabel
        ? this.headingLabel.replace("%{location}", this.lastSearchLabel)
        : this.headingFallback
    }</h3>`;

    const filteredStores = stores.filter(
      (store) => this.getAvailabilityStatus(store.store_id) === "In Stock"
    );

    if (!filteredStores.length) {
      resultsContainer.innerHTML =
        heading + `<p class="no-results">${this.noResultsLabel}</p>`;
      return;
    }

    const countLine = `<p class="store-count">
      ${filteredStores.length} ${this.storeCountLabel}${
      filteredStores.length !== 1 ? "s" : ""
    } ${this.storeFoundLabel}
    </p>`;

    resultsContainer.innerHTML =
      heading +
      countLine +
      filteredStores
        .map((store) => {
          const status = this.getAvailabilityStatus(store.store_id);
          const statusLabel =
            status === "In Stock" ? this.inStockLabel : this.outOfStockLabel;
          const baseUrl = this.isFrench
            ? ""
            : "/en";
          const storePageLink = `${baseUrl}${store.store_page_link}`;

          return `
            <div class="store-result" data-location-id="${store.store_id}">
              <h4 class="store-name flex items-baseline justify-start gap-x-2">
                ${store.name || "Unnamed Store"}            
              </h4>
              <div class="flex justify-between gap-10">
              <div class="address-block">
              <strong>${this.addressLabel}</strong>
              <p class="store-address flex gap-2 mt-0">
                <span>${this.mapIcon}</span>${store.address1} <br/> ${
            store.city
          }, ${store.province}, ${store.zip}
              </p>
              <p class="flex gap-2"><span>${this.phoneIcon}</span>${
            store.phone || ""
          }</p>
              </div>
              <p class="flex flex-col justify-center items-end">
                <strong class="availability-label">${
                  this.availabilityLabel
                }</strong>
                <span class="stock-status" aria-live="polite">${statusLabel}</span>
              </p>
              </div>
              <div class="flex gap-2">
              <a href="${
                store.google_maps_url
              }" target="_blank" rel="noopener" class="btn btn--primary">
                ${this.buttonText1}
              </a>
              <a href="${storePageLink}" target="_blank" rel="noopener" class="btn btn--primary">
                ${this.buttonText2}
              </a>
              </div>
            </div>
          `;
        })
        .join("");
  }
}

customElements.define("store-locator", StoreLocator);

class StoreLocatorDrawer extends DrawerComponent {
  constructor() {
    super();
  }

  show() {
    super.show();

    const currentVariantEl = document.querySelector("[name='id']");
    const productData = JSON.parse(
      document.querySelector("#product-json")?.textContent || "{}"
    );

    if (!currentVariantEl || !productData?.variants) return;

    const variant = productData.variants.find(
      (v) => String(v.id) === String(currentVariantEl.value)
    );

    if (variant) {
      document.dispatchEvent(
        new CustomEvent("variant:changed", {
          detail: { variant },
        })
      );
    }
  }
}

customElements.define("store-locator-drawer", StoreLocatorDrawer);
